'use client'

import { queries } from '@/shared/lib/tanstack-query/query-keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDate, subMonths, subWeeks } from 'date-fns'
import { isEmpty, isUndefined } from 'lodash'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

// Define the types for the duration and the context value
export type DurationType = '2주' | '2개월' | '6개월'
export type ProductDetailPriceHistoriesContextType = {
  duration?: DurationType
  onChangeDuration: (duration: DurationType) => void
  histories: {
    id: string
    price: number
    priceHigh: number
    priceLow: number
    currentDate: string
  }[]
  highestData: {
    id: string
    price: number
    priceHigh: number
    priceLow: number
    currentDate: string
  }
  lowestData: {
    id: string
    price: number
    priceHigh: number
    priceLow: number
    currentDate: string
  }
  lastData: {
    id: string
    price: number
    priceHigh: number
    priceLow: number
    currentDate: string
  }
  averagePrice: number
  isPending: boolean
  isShow: boolean
  setIsShow: (isShow: boolean) => void
  isDataEmpty: boolean
}

// Create the context with default values
const ProductDetailPriceHistoriesContext = createContext<
  ProductDetailPriceHistoriesContextType | undefined
>(undefined)

// Create the provider component
export const ProductDetailPriceHistoriesProvider = ({
  productId,
  defaultDuration = '6개월',
  children,
}: {
  productId: string
  defaultDuration: DurationType
  children: ReactNode
}) => {
  const [duration, setDuration] = useState<DurationType>(defaultDuration)
  const [isShow, setIsShow] = useState(true)
  const calculateStateDate = (duration: DurationType): string => {
    const today = new Date()

    switch (duration) {
      case '2주':
        return formatDate(subWeeks(today, 2), 'yyyy-MM-dd')
      case '2개월':
        return formatDate(subMonths(today, 2), 'yyyy-MM-dd')
      case '6개월':
        return formatDate(subMonths(today, 6), 'yyyy-MM-dd')
      default:
        return formatDate(subMonths(today, 6), 'yyyy-MM-dd')
    }
  }

  const { data, isPending } = useQuery(
    queries.products.histories({
      id: productId,
      stateDate: calculateStateDate(duration),
      endDate: formatDate(new Date(), 'yyyy-MM-dd'),
    }),
  )

  const queryClient = useQueryClient()
  useEffect(() => {
    queryClient.prefetchQuery(
      queries.products.histories({
        id: productId,
        stateDate: calculateStateDate('2주'),
        endDate: formatDate(new Date(), 'yyyy-MM-dd'),
      }),
    )
    queryClient.prefetchQuery(
      queries.products.histories({
        id: productId,
        stateDate: calculateStateDate('2개월'),
        endDate: formatDate(new Date(), 'yyyy-MM-dd'),
      }),
    )
  }, [productId, queryClient])

  const onChangeDuration = useCallback((duration: DurationType) => {
    setDuration(duration)
  }, [])

  const sortedHistories = useMemo(() => {
    if (isPending || !data || isEmpty(data.results)) {
      return [
        {
          id: '',
          price: 0,
          priceHigh: 0,
          priceLow: 0,
          currentDate: Date.now().toLocaleString(),
        },
        {
          id: '',
          price: 0,
          priceHigh: 0,
          priceLow: 0,
          currentDate: Date.now().toLocaleString(),
        },
      ]
    }
    const histories = data.results
    return [...histories].sort(
      (a, b) =>
        new Date(a.currentDate).getTime() - new Date(b.currentDate).getTime(),
    )
  }, [data, isPending])

  const isDataEmpty = useMemo(
    () => (isUndefined(data) ? true : data.results.length === 0),
    [data],
  )

  const highestData = sortedHistories.reduce(
    (max, item) => (item.priceHigh > max.priceHigh ? item : max),
    sortedHistories[0],
  )

  const lowestData = sortedHistories.reduce(
    (min, item) => (item.priceLow < min.priceLow ? item : min),
    sortedHistories[0],
  )

  const lowestAndLatestData =
    sortedHistories.findLast(
      ({ priceLow }) => priceLow === lowestData.priceLow,
    ) ?? lowestData

  const averagePrice = useMemo(
    () =>
      Math.round(
        sortedHistories.reduce((sum, item) => sum + item.priceHigh, 0) /
          sortedHistories.length,
      ),
    [sortedHistories],
  )

  const lastData = useMemo(
    () =>
      sortedHistories.length > 0
        ? sortedHistories[sortedHistories.length - 1]
        : {
            id: '',
            price: 0,
            priceHigh: 0,
            priceLow: 0,
            currentDate: '',
          },
    [sortedHistories],
  )

  return (
    <ProductDetailPriceHistoriesContext.Provider
      value={{
        isShow,
        setIsShow,
        duration,
        onChangeDuration,
        isDataEmpty,
        histories: sortedHistories,
        highestData,
        lowestData: lowestAndLatestData,
        averagePrice,
        lastData,
        isPending,
      }}
    >
      {children}
    </ProductDetailPriceHistoriesContext.Provider>
  )
}

// Custom hook to use the ProductDetailPriceHistoriesContext
export const useProductDetailPriceHistories = () => {
  const context = useContext(ProductDetailPriceHistoriesContext)

  if (context === undefined) {
    throw new Error(
      'useProductDetailPriceHistories must be used within a ProductDetailPriceHistoriesProvider',
    )
  }

  return context
}
